import { useCallback, useEffect, useMemo, useState } from "react";

import { Header } from "@magnetic/header";

import { Button } from "src/components/buttons";
import { CopyColumn } from "src/components/tables/columnConfigs/copyColumn";
import { GenericTypeColumn } from "src/components/tables/columnConfigs/genericTypeColumn";
import { HttpMethodColumn } from "src/components/tables/columnConfigs/httpMethodColumn";
import { HttpStatusColumn } from "src/components/tables/columnConfigs/httpStatusColumn";
import { LatencyColumn } from "src/components/tables/columnConfigs/latencyColumn";
import { StringColumn } from "src/components/tables/columnConfigs/stringColumn";
import { TimeColumn } from "src/components/tables/columnConfigs/timeColumn";
import TypedTable, { TableData } from "src/components/typedTable";
import { DataViewerType } from "src/dataTypes/dataViewerType";
import { RestApiContext } from "src/utils/api/rest/rest";

import { MonitorOverlay } from "./monitorOverlay";

const POLL_INTERVAL_MS = 500;

const RestHistoryMonitorTable = (props: {
  readonly data: RestApiContext[];
  readonly showRoot: boolean;
}) => {
  const columns = useMemo(() => {
    return {
      method: new HttpMethodColumn({
        label: "Method"
      }),
      url: new StringColumn({
        label: "URL"
      }),
      status: new HttpStatusColumn({
        label: "Status"
      }),
      durationMs: new LatencyColumn({
        label: "Duration"
      }),
      requestTs: new TimeColumn({
        label: "Request at"
      }),
      requestBodyData: new GenericTypeColumn<DataViewerType>({
        label: "Request Body"
      }),
      copyReq: new CopyColumn({
        label: "Copy"
      }),
      responseTs: new TimeColumn({
        label: "Response at"
      }),
      responseData: new GenericTypeColumn<DataViewerType>({
        label: "Response"
      }),
      copyResp: new CopyColumn({
        label: "Copy"
      })
    };
  }, []);

  const [tableData, setTableData] = useState<TableData<typeof columns>[]>([]);

  useEffect(() => {
    const newTableData = props.data
      .map((d) => {
        let parsedBody: object | string | undefined;
        if (d.requestBody) {
          try {
            parsedBody = JSON.parse(d.requestBody);
          } catch {
            parsedBody = d.requestBody;
          }
        }
        return {
          ...d,
          requestBodyData: new DataViewerType({
            width: 200,
            data: parsedBody ?? {},
            hideRoot: !props.showRoot,
            expandDepth: 0
          }),
          copyReq:
            typeof parsedBody === "string"
              ? { value: parsedBody }
              : parsedBody ?? {},
          responseData: new DataViewerType({
            width: 350,
            data: d.response,
            hideRoot: !props.showRoot,
            expandDepth: 0
          }),
          copyResp: d.response
        };
      })
      .reverse();
    setTableData(newTableData);
  }, [props.data, props.showRoot]);

  return (
    <TypedTable name="restHistoryMonitor" columns={columns} data={tableData} />
  );
};

type RestHistoryMonitorProps = {
  readonly isOpen: boolean;
  readonly onToggle: () => void;
};

export const RestHistoryMonitor = (
  props: RestHistoryMonitorProps
): JSX.Element => {
  const { isOpen, onToggle } = props;
  const [restHistoryState, setRestHistoryState] = useState<RestApiContext[]>(
    []
  );
  const [showRoot, setShowRoot] = useState<boolean>(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setRestHistoryState((prev) => {
        if (window.cnc.restHistory !== prev) {
          return window.cnc.restHistory;
        }
        return prev;
      });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const handleClear = useCallback(() => {
    window.cnc.restHistory = [];
  }, []);

  const actions = (
    <>
      <Button
        name="clear-rest-history"
        size="sm"
        kind="tertiary"
        onClick={handleClear}
      >
        Clear history
      </Button>
      <Button
        name="toggle-rest-root"
        size="sm"
        kind="tertiary"
        onClick={() => setShowRoot(!showRoot)}
      >
        {!showRoot ? "Collapse " : "Expand "} Root
      </Button>
    </>
  );

  return (
    <>
      <Header.Button
        icon="downloads"
        label="REST History"
        alert={restHistoryState.length}
        onClick={onToggle}
      />
      <MonitorOverlay
        title="REST history"
        isOpen={isOpen}
        onClose={onToggle}
        actions={actions}
      >
        <RestHistoryMonitorTable data={restHistoryState} showRoot={showRoot} />
      </MonitorOverlay>
    </>
  );
};
